/**
 * useFullscreen — a thin wrapper over the browser Fullscreen API for the POS
 * surfaces (the bar-facing sell screen and the guest-facing display), both of
 * which run standalone on a till / a customer monitor where browser chrome is
 * noise. Entering fullscreen REQUIRES a user gesture, so this is driven from a
 * button's onClick — never automatically.
 *
 * `target` is the element to take fullscreen (defaults to the document root, so
 * the whole page goes fullscreen). Returns the live `isFullscreen` flag, a
 * `toggle`, explicit `enter`/`exit`, and `supported` (false on the rare browser
 * without the API — the caller hides the control).
 */
import { useCallback, useEffect, useState } from "react";

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

const fsDoc = (): FsDocument => document as FsDocument;

const currentElement = (): Element | null => {
  const d = fsDoc();
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
};

export interface UseFullscreen {
  isFullscreen: boolean;
  supported: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
}

export function useFullscreen(
  target?: React.RefObject<HTMLElement> | null,
): UseFullscreen {
  const supported =
    typeof document !== "undefined" &&
    (document.fullscreenEnabled ||
      Boolean((document as FsDocument).webkitFullscreenElement !== undefined));
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    () => currentElement() !== null,
  );

  // Reflect changes from any source (Esc, F11, the OS) — not just our button.
  useEffect(() => {
    const sync = () => setIsFullscreen(currentElement() !== null);
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  const enter = useCallback(async () => {
    const el = (target?.current ?? document.documentElement) as FsElement;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch {
      /* user denied / not allowed — leave the flag as the event reports it. */
    }
  }, [target]);

  const exit = useCallback(async () => {
    const d = fsDoc();
    if (currentElement() === null) return;
    try {
      if (d.exitFullscreen) await d.exitFullscreen();
      else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
    } catch {
      /* ignore — the event listener keeps the flag honest. */
    }
  }, []);

  const toggle = useCallback(async () => {
    if (currentElement() === null) await enter();
    else await exit();
  }, [enter, exit]);

  return { isFullscreen, supported, enter, exit, toggle };
}
